import React from 'react';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircleIcon, Loader2 } from 'lucide-react';
import Helper from '@/lib/helper';
import { getSigningClientOrigin } from '@/lib/signingPortal';
import type { DocumentSigningMode } from './signingTypes';

interface SigningDetailsDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    signatoryDetails: any[];
    setSignatoryDetails: React.Dispatch<React.SetStateAction<any[]>>;
    signingMode: DocumentSigningMode;
    setSigningMode: React.Dispatch<React.SetStateAction<DocumentSigningMode>>;
    sending: boolean;
    onSubmit: (e: any) => Promise<void> | void;
    /**
     * Choose who puts the company stamp. Called with the signer's position in
     * the list; the page updates the document so the stamp box follows. Each
     * signature section has at most one stamper.
     */
    onStampChange?: (index: number, on: boolean) => void;
    /** Which signers may stamp. By default, any signer who is not a witness. */
    canStamp?: (signatory: any) => boolean;
}

const SigningDetailsDialog: React.FC<SigningDetailsDialogProps> = ({
    open,
    onOpenChange,
    signatoryDetails,
    setSignatoryDetails,
    signingMode,
    setSigningMode,
    sending,
    onSubmit,
    onStampChange,
    canStamp = (signatory: any) => signatory.type !== 'witness',
}) => {
    const stampers = signatoryDetails.filter(signatory => signatory.fields?.stamp).map(signatory => signatory.label);
    const updateSignatory = (index: number, patch: Record<string, any>) => {
        setSignatoryDetails(prev => prev.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item));
    };

    const clientPortalOrigin = getSigningClientOrigin();
    const normalizedEmails = signatoryDetails.map(signatory => String(signatory.email || '').trim().toLowerCase());
    const duplicateEmails = normalizedEmails.filter((email, index) => email && normalizedEmails.indexOf(email) !== index);
    const duplicateEmailSet = new Set(duplicateEmails);
    const nameLooksLikeEmail = (name: string) => Helper.isValidEmail(String(name || '').trim());

    const hasInvalidSignatory = signatoryDetails.some(signatory => {
        const name = String(signatory.name || '').trim();
        const email = String(signatory.email || '').trim().toLowerCase();
        if (!name || nameLooksLikeEmail(name) || !Helper.isValidEmail(email) || duplicateEmailSet.has(email)) return true;
        return false;
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogTitle className="mt-5">Complete Signatory Information</DialogTitle>
                <Alert className="mb-4">
                    <AlertCircleIcon className="mr-2" />
                    <p className="text-sm">
                        {signingMode === 'docusign'
                            ? 'This document will be sent to all signatories below in order. Please ensure each name and email is accurate before sending.'
                            : 'This document will be sent using a local signing link. Each signer will complete their own signature and required fields when they open the link.'}
                    </p>
                </Alert>

                {signingMode === 'local' && (
                    <Alert className="mb-4 border-amber-200 bg-amber-50">
                        <AlertCircleIcon className="mr-2 text-amber-700" />
                        <p className="text-sm text-amber-900">
                            {clientPortalOrigin
                                ? `Signers will open links on the customer portal (${clientPortalOrigin}). Ensure that app hosts the same signing route and points to this API.`
                                : 'Set VITE_CLIENT_PORTAL_URL in this build so the server can email signing links to the customer portal (xpillar), not the agent URL.'}
                        </p>
                    </Alert>
                )}

                <div className="mb-4 flex flex-wrap gap-2 rounded-lg border bg-slate-50 p-2">
                    <Button
                        type="button"
                        size="sm"
                        variant={signingMode === 'local' ? 'default' : 'outline'}

                        onClick={() => setSigningMode('local')}
                    >
                        Local Signing (default)
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        variant={signingMode === 'docusign' ? 'default' : 'outline'}

                        onClick={() => setSigningMode('docusign')}
                    >
                        DocuSign
                    </Button>
                </div>

                <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
                    <h4 className="mb-2 text-sm font-semibold">Signing Order</h4>
                    <p className="mb-2 text-xs text-slate-700">
                        <span className="font-semibold">Company stamp: </span>
                        {stampers.length ? stampers.join(', ') : 'nobody stamps this document'}
                    </p>
                    <ol className="ml-6 space-y-1 text-xs">
                        {signatoryDetails.map((sig, idx) => (
                            <li key={`${sig.label}-${idx}`} className="list-decimal">
                                <strong>{sig.label}</strong>
                                {sig.name && ` - ${sig.name}`}
                            </li>
                        ))}
                    </ol>
                </div>

                <div className="space-y-4">
                    {signatoryDetails.map((signatory, idx) => (
                        <div key={`${signatory.label}-${idx}`} className="rounded-lg border bg-gray-50 p-4">
                            <div className="mb-3 flex items-center">
                                <Badge variant={signatory.type === 'witness' ? 'secondary' : 'default'} className="text-xs">
                                    {signatory.label}
                                </Badge>
                                <span className="ml-2 text-xs text-gray-600">
                                    (Signs {idx + 1}{idx === 0 ? 'st' : idx === 1 ? 'nd' : idx === 2 ? 'rd' : 'th'})
                                </span>
                            </div>

                            <div className="space-y-3">
                                <div>
                                    <Label className="mb-1 text-xs">Full Name *</Label>
                                    <Input
                                        placeholder={`Enter ${signatory.label}'s full name`}
                                        value={signatory.name}
                                        onChange={(e) => updateSignatory(idx, { name: e.target.value })}
                                        className={!String(signatory.name || '').trim() || nameLooksLikeEmail(signatory.name) ? 'border-red-400' : ''}
                                    />
                                    {nameLooksLikeEmail(signatory.name) && (
                                        <p className="mt-1 text-xs text-red-500">Use the signer&apos;s name here, not their email address.</p>
                                    )}
                                </div>

                                <div>
                                    <Label className="mb-1 text-xs">Email Address *</Label>
                                    <Input
                                        type="email"
                                        placeholder={`Enter ${signatory.label}'s email`}
                                        value={signatory.email}
                                        onChange={(e) => updateSignatory(idx, { email: e.target.value })}
                                        className={!Helper.isValidEmail(signatory.email) || duplicateEmailSet.has(String(signatory.email || '').trim().toLowerCase()) ? 'border-red-400' : ''}
                                    />
                                    {duplicateEmailSet.has(String(signatory.email || '').trim().toLowerCase()) && (
                                        <p className="mt-1 text-xs text-red-500">This email is already used by another signer.</p>
                                    )}
                                </div>

                                {onStampChange && canStamp(signatory) && (
                                    <label className="flex cursor-pointer items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4"
                                            checked={!!signatory.fields?.stamp}
                                            onChange={(e) => onStampChange(idx, e.target.checked)}
                                        />
                                        <span>Puts the company stamp</span>
                                    </label>
                                )}

                                {signingMode === 'local' && (
                                    <div className="rounded-md border border-dashed border-slate-300 bg-white px-3 py-3 text-xs text-slate-600">
                                        This signer will fill in the required document fields, signature{signatory.fields.stamp ? ', and stamp' : ''} from the signing link.
                                    </div>
                                )}

                                {signingMode === 'local' && (
                                    <div className="text-xs text-gray-600">
                                        <span className="font-semibold">Required fields: </span>
                                        {Object.entries(signatory.fields)
                                            .filter(([_, enabled]) => enabled)
                                            .map(([field]) => field.charAt(0).toUpperCase() + field.slice(1))
                                            .join(', ')}
                                        {`${Object.values(signatory.fields).some(Boolean) ? ', ' : ''}Completed in signing link`}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <DialogFooter className="mt-6 flex justify-end gap-2">
                    <Button
                        variant="outline"
                        onClick={() => {
                            onOpenChange(false);
                            setSignatoryDetails([]);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button onClick={onSubmit} disabled={sending || hasInvalidSignatory}>
                        {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {sending ? 'Submitting...' : signingMode === 'docusign' ? 'Send for Signing' : 'Send Local Signing Link'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default SigningDetailsDialog;
